import React, { useEffect, useState, useCallback } from 'react';
import { Gauge, Loader, RefreshCw } from 'lucide-react';
import api from '../../services/api';
import alertService from '../../utils/alert';
import PageHeader from '../../components/PageHeader';

const RANGES = [1, 7, 30];
const TIER_BADGE = { read: 'sf-badge-success', write: 'sf-badge-manager', money: 'sf-badge-warning', destructive: 'sf-badge-error' };
const num = (n) => (n || 0).toLocaleString();

function Stat({ label, value }) {
  return (
    <div className="rounded-lg border border-[#DDDBDA] bg-white p-4">
      <div className="text-xs text-[#706E6B]">{label}</div>
      <div className="text-2xl font-semibold text-[#2E2E2E]">{value}</div>
    </div>
  );
}

export default function AgentMeterPage() {
  const [days, setDays] = useState(7);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/support/agent-runs/meter', { params: { days } });
      setData(res.data.data);
    } catch (err) {
      alertService.error(err.response?.data?.error || 'Could not load the meter');
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const totalRuns = data ? Object.values(data.outcomes || {}).reduce((a, b) => a + b, 0) : 0;
  const tokens = data ? (data.models || []).reduce((a, m) => a + m.promptTokens + m.completionTokens, 0) : 0;

  return (
    <div className="p-6 space-y-6">
      <PageHeader icon={Gauge} title="Agent Meter" subtitle="How your AI agent, tools and models are performing" />
      <div className="flex items-center gap-2">
        {RANGES.map((r) => (
          <button key={r} onClick={() => setDays(r)} className={`px-3 py-1 rounded-full text-sm border ${days === r ? 'bg-[#0176D3] text-white border-[#0176D3]' : 'border-[#DDDBDA] text-[#444]'}`}>
            {r === 1 ? '24 hours' : `${r} days`}
          </button>
        ))}
        <button onClick={load} className="ml-auto text-[#706E6B]" title="Refresh"><RefreshCw size={16} /></button>
      </div>

      {loading && !data ? <Loader className="animate-spin" /> : data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <Stat label="Agent runs" value={num(totalRuns)} />
            <Stat label="Escalated" value={num(data.outcomes?.escalated)} />
            <Stat label="Tokens used" value={num(tokens)} />
            <Stat label="Credits left" value={data.credits ? num(data.credits.total) : '—'} />
            <Stat label="Credits spent" value={num(Object.values(data.creditsByKind || {}).reduce((a, k) => a + (k.credits || 0), 0))} />
          </div>

          <section>
            <h3 className="text-sm font-semibold mb-2">Tools</h3>
            <div className="overflow-x-auto rounded-lg border border-[#DDDBDA] bg-white">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[#706E6B]"><tr><th className="p-2">Tool</th><th>Tier</th><th>Calls</th><th>Success</th><th>Errors</th><th>Awaiting approval</th><th>Avg ms</th><th>Last error</th></tr></thead>
                <tbody>
                  {(data.tools || []).map((t) => (
                    <tr key={t.name} className="border-t border-[#EEE]">
                      <td className="p-2 font-mono">{t.name}</td>
                      <td>{t.tier ? <span className={`sf-badge ${TIER_BADGE[t.tier] || ''}`}>{t.tier}</span> : '—'}</td>
                      <td>{num(t.calls)}</td>
                      <td>{t.successRate == null ? '—' : `${t.successRate}%`}</td>
                      <td className={t.errors ? 'text-[#BA0517]' : ''}>{num(t.errors)}</td>
                      <td>{num(t.pending)}</td>
                      <td>{num(t.avgMs)}</td>
                      <td className="text-[#BA0517] truncate max-w-[220px]">{t.lastError || ''}</td>
                    </tr>
                  ))}
                  {!data.tools?.length && <tr><td className="p-4 text-[#706E6B]" colSpan={8}>No tool calls in this period yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold mb-2">Models</h3>
            <div className="overflow-x-auto rounded-lg border border-[#DDDBDA] bg-white">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-[#706E6B]"><tr><th className="p-2">Provider</th><th>Model</th><th>Calls</th><th>Prompt tokens</th><th>Completion tokens</th><th>Errors</th><th>Avg ms</th></tr></thead>
                <tbody>
                  {(data.models || []).map((m) => (
                    <tr key={`${m.provider}-${m.model}`} className="border-t border-[#EEE]">
                      <td className="p-2">{m.provider}</td><td className="font-mono">{m.model}</td><td>{num(m.calls)}</td>
                      <td>{num(m.promptTokens)}</td><td>{num(m.completionTokens)}</td><td>{num(m.errors)}</td><td>{num(m.avgMs)}</td>
                    </tr>
                  ))}
                  {!data.models?.length && <tr><td className="p-4 text-[#706E6B]" colSpan={7}>No model calls in this period yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </section>

          <section className="grid md:grid-cols-2 gap-3">
            <div className="rounded-lg border border-[#DDDBDA] bg-white p-4">
              <h3 className="text-sm font-semibold mb-2">Outcomes</h3>
              {Object.entries(data.outcomes || {}).map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span>{k}</span><span>{num(v)}</span></div>)}
              {!Object.keys(data.outcomes || {}).length && <div className="text-sm text-[#706E6B]">No runs yet.</div>}
            </div>
            <div className="rounded-lg border border-[#DDDBDA] bg-white p-4">
              <h3 className="text-sm font-semibold mb-2">Channels</h3>
              {Object.entries(data.channels || {}).map(([k, v]) => <div key={k} className="flex justify-between text-sm"><span>{k}</span><span>{num(v)}</span></div>)}
              {!Object.keys(data.channels || {}).length && <div className="text-sm text-[#706E6B]">No runs yet.</div>}
            </div>
          </section>
        </>
      )}
    </div>
  );
}
